/**
 * Complete Vocabulary Index
 * 合并所有词汇数据
 */

// Import all vocabulary sources
import { vocabularyData, VocabularyWord, vocabularyCategories } from './vocabulary';
import { extendedVocabularyData } from './vocabulary-extended';
import { elementarySupplementWords } from './vocabulary-elementary-supplement';
import { elementarySupplement2Words } from './vocabulary-elementary-supplement2';
import { monthsVocabulary } from './vocabulary-months';
import { directionsVocabulary } from './vocabulary-directions';
import { elementaryMissingWords } from './vocabulary-elementary-missing';
import { middleSchoolVocabulary } from './vocabulary-middle-school';
import { grade7Vocabulary } from './vocabulary-grade7';
import { grade8Vocabulary } from './vocabulary-grade8';
import { grade9Vocabulary } from './vocabulary-grade9';
import { vocabularyHM } from './vocabulary-hm';
import { vocabularyLN } from './vocabulary-ln';
import { vocabularyOZ } from './vocabulary-oz';
import { additionalVocabularyRound1 } from './vocabulary-additional-round1';
import { additionalVocabularyRound2 } from './vocabulary-additional-round2';

// Combine all vocabulary into one complete array with deduplication
// Use Map to remove duplicates by word field (case-insensitive)
const allVocabulary = [
  ...vocabularyData,
  ...monthsVocabulary,              // Complete months vocabulary (prioritized)
  ...extendedVocabularyData,
  ...elementarySupplementWords,
  ...elementarySupplement2Words,
  ...directionsVocabulary,
  ...elementaryMissingWords,
  ...middleSchoolVocabulary,
  ...grade7Vocabulary,
  ...grade8Vocabulary,
  ...grade9Vocabulary,
  ...vocabularyHM,
  ...vocabularyLN,
  ...vocabularyOZ,
  ...additionalVocabularyRound1,
  ...additionalVocabularyRound2,
];

// Deduplicate: keep first occurrence of each word
const wordMap = new Map<string, VocabularyWord>();
allVocabulary.forEach(word => {
  const key = word.word.toLowerCase().trim();
  if (!wordMap.has(key)) {
    wordMap.set(key, word);
  }
});

export const completeVocabularyData: VocabularyWord[] = Array.from(wordMap.values());

// Re-export types and categories
export type { VocabularyWord } from './vocabulary';
export { vocabularyCategories } from './vocabulary';

// Helper functions for filtering
export function getWordsByLevel(level: 'elementary' | 'middle'): VocabularyWord[] {
  const elementaryCategories = ['animals', 'food', 'family', 'colors', 'numbers', 'months', 'body', 'actions', 'school', 'weather', 'emotions', 'directions', 'daily_life', 'transport', 'places', 'time', 'clothes', 'sports'];

  return completeVocabularyData.filter(word => {
    // If word has explicit level, use it
    if (word.level) return word.level === level;

    // Otherwise infer from category
    if (level === 'elementary') {
      return elementaryCategories.includes(word.category);
    } else {
      return !elementaryCategories.includes(word.category);
    }
  });
}

export function getWordsByGrade(grade: '3' | '4' | '5' | '6' | '7' | '8' | '9'): VocabularyWord[] {
  return completeVocabularyData.filter(word => {
    // If word has explicit grade, use it
    if (word.grade) return word.grade === grade;

    // Otherwise infer from difficulty and category
    const inferredGrade = inferGrade(word);
    return inferredGrade === grade;
  });
}

// Helper function to infer grade
function inferGrade(word: VocabularyWord): '3' | '4' | '5' | '6' | '7' | '8' | '9' {
  const elementaryCategories = ['animals', 'food', 'family', 'colors', 'numbers', 'months', 'body', 'actions', 'school', 'weather', 'emotions', 'directions', 'daily_life', 'transport', 'places', 'time', 'clothes', 'sports'];

  // Elementary school (3-6)
  if (elementaryCategories.includes(word.category)) {
    if (word.difficulty === 'easy') {
      // Grade 3-4: basic words
      const grade3Categories = ['animals', 'family', 'colors', 'numbers', 'body', 'food'];
      const grade4Categories = ['months', 'actions', 'school', 'emotions', 'directions', 'places'];
      if (grade3Categories.includes(word.category)) return '3';
      if (grade4Categories.includes(word.category)) return '4';
      return '3';
    } else if (word.difficulty === 'medium') {
      // Grade 5-6: intermediate words
      const grade5Categories = ['daily_life', 'transport', 'time', 'directions'];
      const grade6Categories = ['clothes', 'sports', 'weather'];
      if (grade5Categories.includes(word.category)) return '5';
      if (grade6Categories.includes(word.category)) return '6';
      return '5';
    }
  }

  // Middle school (7-9)
  if (word.difficulty === 'easy') {
    return '7';
  } else if (word.difficulty === 'medium') {
    const grade7Categories = ['science', 'technology'];
    const grade8Categories = ['nature', 'hobbies', 'jobs', 'travel'];
    if (grade7Categories.includes(word.category)) return '7';
    if (grade8Categories.includes(word.category)) return '8';
    return '7';
  } else {
    return '9'; // Hard difficulty
  }
}

// Get statistics
export function getVocabularyStats() {
  return {
    total: completeVocabularyData.length,
    elementary: getWordsByLevel('elementary').length,
    middle: getWordsByLevel('middle').length,
    byGrade: {
      '3': getWordsByGrade('3').length,
      '4': getWordsByGrade('4').length,
      '5': getWordsByGrade('5').length,
      '6': getWordsByGrade('6').length,
      '7': getWordsByGrade('7').length,
      '8': getWordsByGrade('8').length,
      '9': getWordsByGrade('9').length,
    },
    byDifficulty: {
      easy: completeVocabularyData.filter(w => w.difficulty === 'easy').length,
      medium: completeVocabularyData.filter(w => w.difficulty === 'medium').length,
      hard: completeVocabularyData.filter(w => w.difficulty === 'hard').length,
    }
  };
}
